import { uniqueId } from 'lodash'
import { MAX_OUTPUT_VIDEO_ASPECT_RATIO, MIN_OUTPUT_VIDEO_ASPECT_RATIO, MIN_OUTPUT_VIDEO_DIMENSION } from '../constants'
import { CreationToolSourceFile } from '../types'
import CreationToolError from '../types/error'
import CreationToolFileType from '../types/file-type'
import getSourceInfo, { getUrlExtension, getUrlMimeType } from './get-source-info'
import getVideoInfo from './get-video-info'

const checkIfVideoHasAudio = (video: any) =>
    !!video.mozHasAudio || !!video.webkitAudioDecodedByteCount || !!(video.audioTracks && video.audioTracks.length)

const validateVideo = (
    source: File | string,
    minDuration: number,
    maxDuration: number,
    requiresAudio: boolean,
    withCaption: boolean,
    quality = 'hd'
): Promise<CreationToolSourceFile> =>
    new Promise(async (resolve, reject) => {
        const { isVideo } = getSourceInfo(source)

        // if source is not a video, throw error
        if (!isVideo) {
            reject(CreationToolError.InvalidVideoFile)
            return
        }

        const mimeType = typeof source === 'string' ? getUrlMimeType(source) : source.type
        const preview = document.createElement('canvas')

        // youtube/vimeo urls
        if (typeof source === 'string' && (mimeType === 'youtube' || mimeType === 'vimeo')) {
            try {
                // fetch duration and privacy information for external video
                const { duration, title, width, height, isPrivate, hasCaptions, languages } = await getVideoInfo(
                    source,
                    withCaption,
                    quality
                )
                const aspectRatio = width / height

                preview.width = width
                preview.height = height

                if (isPrivate) {
                    // video is private
                    reject(CreationToolError.VideoPrivate)
                } else if (duration <= 0) {
                    // video is live stream
                    reject(CreationToolError.VideoLiveStream)
                } else if (maxDuration && duration > maxDuration) {
                    // video is too long
                    reject(CreationToolError.VideoTooLong)
                    // TODO: hook up ability to trim video from bulk
                    // } else if (duration > maxDuration) {
                    //     // video needs trimming
                    //     reject(CreationToolError.VideoNeedsTrim)
                } else if (duration < minDuration) {
                    // video is too short
                    reject(CreationToolError.VideoTooShort)
                } else if (requiresAudio && width < MIN_OUTPUT_VIDEO_DIMENSION) {
                    // video width too small
                    reject(CreationToolError.VideoWidthTooSmall)
                } else if (requiresAudio && height < MIN_OUTPUT_VIDEO_DIMENSION) {
                    // video height too small
                    reject(CreationToolError.VideoHeightTooSmall)
                } else if (requiresAudio && aspectRatio < MIN_OUTPUT_VIDEO_ASPECT_RATIO) {
                    // video aspect ratio too small
                    reject(CreationToolError.VideoAspectRatioTooSmall)
                } else if (requiresAudio && aspectRatio > MAX_OUTPUT_VIDEO_ASPECT_RATIO) {
                    // video aspect ratio too big
                    reject(CreationToolError.VideoAspectRatioTooBig)
                } else {
                    resolve({
                        id: uniqueId(),
                        source,
                        frames: [],
                        width,
                        height,
                        duration,
                        type: CreationToolFileType.VIDEO_EMBED,
                        sourceUrl: source,
                        title,
                        preview,
                        hasAudio: true,
                        isTransparent: false,
                        isAnimated: duration > 0,
                        isUrl: true,
                        hasCaptions,
                        languages,
                    })
                }
            } catch (err) {
                reject(CreationToolError.InvalidVideoUrl)
            }

            return
        }

        // video file or url
        const video = document.createElement('video')

        video.onloadeddata = () => {
            const width = video.videoWidth || 1
            const height = video.videoHeight || 1

            const aspectRatio = width / height

            if (maxDuration && video.duration > maxDuration) {
                // video is too long
                reject(CreationToolError.VideoTooLong)
                // TODO: hook up ability to trim video from bulk
                // } else if (video.duration > maxDuration) {
                //     // video needs trimming
                //     reject(CreationToolError.VideoNeedsTrim)
            } else if (video.duration < minDuration) {
                // video is too short
                reject(CreationToolError.VideoTooShort)
            } else if (requiresAudio && !checkIfVideoHasAudio(video)) {
                // video has no audio
                reject(CreationToolError.NoAudio)
            } else if (requiresAudio && width < MIN_OUTPUT_VIDEO_DIMENSION) {
                if (source instanceof File && width === 1 && height === 1) {
                    const isMov = getUrlExtension(source.name).toLowerCase() === `.mov`
                    if (isMov) {
                        reject(CreationToolError.InvalidMOVFile)
                    }
                }
                // video width too small
                reject(CreationToolError.VideoWidthTooSmall)
            } else if (requiresAudio && height < MIN_OUTPUT_VIDEO_DIMENSION) {
                // video height too small
                reject(CreationToolError.VideoHeightTooSmall)
            } else if (requiresAudio && aspectRatio < MIN_OUTPUT_VIDEO_ASPECT_RATIO) {
                // video aspect ratio too small
                reject(CreationToolError.VideoAspectRatioTooSmall)
            } else if (requiresAudio && aspectRatio > MAX_OUTPUT_VIDEO_ASPECT_RATIO) {
                // video aspect ratio too big
                reject(CreationToolError.VideoAspectRatioTooBig)
            } else {
                preview.width = video.videoWidth || 1
                preview.height = video.videoHeight || 1
                preview.getContext('2d')?.drawImage(video, 0, 0)

                resolve({
                    id: uniqueId(),
                    source,
                    frames: [],
                    width,
                    height,
                    preview,
                    duration: video.duration,
                    type: CreationToolFileType.VIDEO,
                    sourceUrl: typeof source === 'string' ? source : undefined,
                    hasAudio: checkIfVideoHasAudio(video),
                    isTransparent: false,
                    isAnimated: video.duration > 0,
                    isUrl: typeof source === 'string',
                })
            }

            // revoke the object url we created
            if (typeof source !== 'string') {
                URL.revokeObjectURL(video.src)
            }
        }

        video.onerror = () => {
            if (mimeType === 'video/webm') {
                reject(CreationToolError.InvalidWebMFile)
            }

            reject(CreationToolError.InvalidVideoFile)

            // revoke the object url we created
            if (typeof source !== 'string') {
                URL.revokeObjectURL(video.src)
            }
        }

        video.preload = 'auto'
        video.src = typeof source === 'string' ? source : URL.createObjectURL(source)
    })

export default validateVideo
